import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import {
  reportSummaryUrl, reportSalesByDayUrl, reportSalesByBranchUrl,
  reportTopProductsUrl, reportInventoryUrl,
  reportSalesByCashierUrl, reportExpensesByCategoryUrl,
  branchesUrl, userPermsUrl,
} from '../../BaseURL';
import { fmtMoney, fmtNumber, fmtDate } from '../../utils/format';

// ─── Date helpers ────────────────────────────────────────────
const isoDay = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d; };
const todayISO = () => isoDay(new Date());
const startOfMonth = () => { const d = new Date(); return isoDay(new Date(d.getFullYear(), d.getMonth(), 1)); };
const startOfYear  = () => { const d = new Date(); return isoDay(new Date(d.getFullYear(), 0, 1)); };

const DATE_PRESETS = [
  { key: '7d',  label: 'Last 7 days',  from: () => isoDay(daysAgo(7)),  to: todayISO },
  { key: '30d', label: 'Last 30 days', from: () => isoDay(daysAgo(30)), to: todayISO },
  { key: '90d', label: 'Last 90 days', from: () => isoDay(daysAgo(90)), to: todayISO },
  { key: 'mtd', label: 'Month-to-date',from: startOfMonth,               to: todayISO },
  { key: 'ytd', label: 'Year-to-date', from: startOfYear,                to: todayISO },
];

// ─── Export helpers ──────────────────────────────────────────
const escapeCsv = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};
const exportCsv = (filename, rows) => {
  if (!rows?.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escapeCsv(r[h])).join(','))].join('\n');
  downloadBlob(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), filename);
};
const xmlEscape = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
const exportXml = (filename, rootName, rowName, rows, meta = {}) => {
  const metaXml = Object.entries(meta).map(([k, v]) => `  <${k}>${xmlEscape(v)}</${k}>`).join('\n');
  const body = rows.map((r) => (
    '  <' + rowName + '>\n' +
    Object.entries(r).map(([k, v]) => `    <${k}>${xmlEscape(v)}</${k}>`).join('\n') +
    '\n  </' + rowName + '>'
  )).join('\n');
  const xml = `<?xml version="1.0" encoding="UTF-8"?>\n<${rootName}>\n${metaXml}${metaXml ? '\n' : ''}${body}\n</${rootName}>`;
  downloadBlob(new Blob([xml], { type: 'application/xml;charset=utf-8;' }), filename);
};
const printThis = (title) => {
  const originalTitle = document.title;
  document.title = title;
  window.print();
  setTimeout(() => { document.title = originalTitle; }, 500);
};

// ─── Report catalog definitions ──────────────────────────────
// Each report has: id, category, title, description, icon, perm (permission key),
// superOnly flag, and the render function that fetches + shows details.
const CATEGORIES = [
  { key: 'sales',     label: 'Sales',     icon: '💵', color: 'brand'   },
  { key: 'inventory', label: 'Inventory', icon: '📦', color: 'accent'  },
  { key: 'finance',   label: 'Financial', icon: '📈', color: 'success' },
  { key: 'ops',       label: 'Operations',icon: '🚚', color: 'warn'    },
];

const REPORTS = [
  { id: 'sales-summary',    cat: 'sales',    title: 'Sales summary',
    perm: 'reports.sales.summary',
    description: 'Headline P&L numbers: revenue, cost of goods, gross profit and net profit for the period.',
    icon: '💰' },
  { id: 'sales-by-day',     cat: 'sales',    title: 'Sales by day',
    perm: 'reports.sales.by_day',
    description: 'Daily revenue trend with a chart and a receipt count per day.',
    icon: '📅' },
  { id: 'top-products',     cat: 'sales',    title: 'Top-selling products',
    perm: 'reports.sales.by_product',
    description: 'Best sellers by revenue with quantity sold, COGS and profit.',
    icon: '🏆' },
  { id: 'sales-by-cashier', cat: 'sales',    title: 'Sales by cashier',
    perm: 'reports.sales.by_cashier',
    description: 'Cashier performance: number of sales, revenue, average ticket, discount given.',
    icon: '👤' },
  { id: 'sales-by-branch',  cat: 'sales',    title: 'Sales by branch',
    perm: 'reports.sales.by_branch',
    description: 'Compare revenue and profit across every branch. Head-office view only.',
    icon: '🏬', superOnly: true },

  { id: 'inventory',        cat: 'inventory',title: 'Current stock',
    perm: 'reports.inventory.current',
    description: 'Stock value (cost & retail), total units on hand, low-stock and out-of-stock items.',
    icon: '📦' },

  { id: 'pnl',              cat: 'finance',  title: 'Profit & Loss',
    perm: 'reports.finance.pnl',
    description: 'Revenue minus COGS minus expenses. Net profit with a breakdown of each component.',
    icon: '💹' },
  { id: 'expenses',         cat: 'finance',  title: 'Expenses by category',
    perm: 'reports.finance.expenses',
    description: 'Where the money went. Pie chart of expenses by category plus a detail table.',
    icon: '💳' },
];

// ─── Root component ──────────────────────────────────────────
const Reports = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isSuper = me?.role === 'super_admin';

  // Effective permissions (super_admin implicitly has all)
  const [effective, setEffective] = useState(new Set());
  const [permsLoaded, setPermsLoaded] = useState(false);

  useEffect(() => {
    if (!me?.id) return;
    if (isSuper) { setPermsLoaded(true); return; }
    axios.get(userPermsUrl(me.id)).then((res) => {
      if (res.data?.success) setEffective(new Set(res.data.effective || []));
    }).catch(() => {}).finally(() => setPermsLoaded(true));
  }, [me, isSuper]);

  const hasPerm = (key) => isSuper || effective.has(key);

  const visibleReports = useMemo(
    () => REPORTS.filter((r) => (isSuper || !r.superOnly) && hasPerm(r.perm)),
    // eslint-disable-next-line
    [effective, isSuper, permsLoaded]
  );

  const [openId, setOpenId] = useState(null);
  const openReport = REPORTS.find((r) => r.id === openId);

  if (!permsLoaded) {
    return <div className="empty-state"><div className="ic">⏳</div><h4>Loading reports…</h4></div>;
  }

  // Report detail view
  if (openReport) {
    return <ReportDetail report={openReport} isSuper={isSuper} me={me} onBack={() => setOpenId(null)} />;
  }

  // Report catalog view — grouped by category
  const grouped = CATEGORIES
    .map((c) => ({ ...c, reports: visibleReports.filter((r) => r.cat === c.key) }))
    .filter((c) => c.reports.length > 0);

  return (
    <>
      <div className="panel">
        <div className="panel-head">
          <div className="head-text">
            <h2>Reports</h2>
            <span className="sub">
              {isSuper
                ? 'Pick a report to run. Every category below can be assigned to individual users or roles from Users & Access.'
                : 'The reports assigned to you by the super administrator. Ask head office if you need access to more.'}
            </span>
          </div>
        </div>

        {visibleReports.length === 0 ? (
          <div className="empty-state">
            <div className="ic">📄</div>
            <h4>No reports are available to you yet</h4>
            <p>Ask the super administrator to assign one of the report permissions to your role or account.</p>
          </div>
        ) : (
          grouped.map((cat) => (
            <div key={cat.key} className="rpt-cat-section">
              <div className="rpt-cat-head">
                <span className={`rpt-cat-ic tone-${cat.color}`}>{cat.icon}</span>
                <div>
                  <div className="rpt-cat-title">{cat.label}</div>
                  <div className="rpt-cat-sub">{cat.reports.length} report{cat.reports.length === 1 ? '' : 's'}</div>
                </div>
              </div>
              <div className="rpt-catalog">
                {cat.reports.map((r) => (
                  <button key={r.id} className={`rpt-card tone-${cat.color}`} onClick={() => setOpenId(r.id)}>
                    <div className="rpt-card-ic">{r.icon}</div>
                    <div className="rpt-card-body">
                      <div className="rpt-card-title">{r.title}</div>
                      <div className="rpt-card-desc">{r.description}</div>
                    </div>
                    <div className="rpt-card-arrow">→</div>
                  </button>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
};

// ─── Report detail — routes to the right renderer by id ─────────────
const ReportDetail = ({ report, isSuper, me, onBack }) => {
  const [rangeKey, setRangeKey] = useState('30d');
  const [range, setRange] = useState({ from: isoDay(daysAgo(30)), to: todayISO() });
  const [branchId, setBranchId] = useState(me?.branch_id || '');
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!isSuper) return;
    axios.get(branchesUrl).then((res) => {
      if (res.data?.success) setBranches(res.data.results || []);
    }).catch(() => {});
  }, [isSuper]);

  const applyPreset = (key) => {
    setRangeKey(key);
    const p = DATE_PRESETS.find((x) => x.key === key);
    if (p) setRange({ from: p.from(), to: p.to() });
  };
  const setCustomFrom = (v) => { setRangeKey('custom'); setRange((r) => ({ ...r, from: v })); };
  const setCustomTo   = (v) => { setRangeKey('custom'); setRange((r) => ({ ...r, to:   v })); };

  const branchName = branches.find((b) => Number(b.id) === Number(branchId))?.name || '';

  return (
    <div>
      {/* ─── Detail header ─── */}
      <div className="panel">
        <div className="panel-head">
          <div className="head-text" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button className="btn-ghost btn-sm" onClick={onBack}>← Back to reports</button>
            <div>
              <h2 style={{ margin: 0 }}>{report.title}</h2>
              <span className="sub">{report.description}</span>
            </div>
          </div>
        </div>

        <div className="sales-filter-bar">
          <div className="segmented">
            {DATE_PRESETS.map((p) => (
              <button
                key={p.key}
                className={`seg ${rangeKey === p.key ? 'active' : ''}`}
                onClick={() => applyPreset(p.key)}
              >{p.label}</button>
            ))}
            <button className={`seg ${rangeKey === 'custom' ? 'active' : ''}`} onClick={() => setRangeKey('custom')}>Custom</button>
          </div>
          <div className="sales-filter-dates">
            <input type="date" className="f-input" value={range.from} onChange={(e) => setCustomFrom(e.target.value)} />
            <span className="muted">to</span>
            <input type="date" className="f-input" value={range.to} onChange={(e) => setCustomTo(e.target.value)} />
          </div>
          {isSuper && branches.length > 0 && (
            <select className="f-select" style={{ minWidth: 180 }} value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">All branches</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* ─── Report renderer ─── */}
      <ReportRenderer id={report.id} range={range} branchId={branchId} branchName={branchName} isSuper={isSuper} />
    </div>
  );
};

// ─── Router that maps id → renderer ─────────────────────────────────
const ReportRenderer = ({ id, range, branchId, branchName, isSuper }) => {
  const commonParams = { from: range.from, to: range.to, ...(branchId ? { branch_id: branchId } : {}) };
  const props = { range, branchId, branchName, isSuper, params: commonParams };
  switch (id) {
    case 'sales-summary':    return <RSalesSummary {...props} />;
    case 'sales-by-day':     return <RSalesByDay {...props} />;
    case 'top-products':     return <RTopProducts {...props} />;
    case 'sales-by-cashier': return <RSalesByCashier {...props} />;
    case 'sales-by-branch':  return <RSalesByBranch {...props} />;
    case 'inventory':        return <RInventory {...props} />;
    case 'pnl':              return <RPnL {...props} />;
    case 'expenses':         return <RExpenses {...props} />;
    default:                 return <div className="empty-state">Unknown report</div>;
  }
};

// ─── Reusable ExportBar ─────────────────────────────────────────
const ExportBar = ({ filename, rootName, rowName, rows, meta, printTitle }) => (
  <div className="rpt-export-bar">
    <button className="btn-ghost btn-sm" disabled={!rows?.length} onClick={() => exportCsv(`${filename}.csv`, rows)}>⬇️ CSV</button>
    <button className="btn-ghost btn-sm" disabled={!rows?.length} onClick={() => exportXml(`${filename}.xml`, rootName, rowName, rows, meta)}>⬇️ XML</button>
    <button className="btn-ghost btn-sm" onClick={() => printThis(printTitle || filename)}>🖨 Print</button>
  </div>
);

// ═══════════════════════════════════════════════════════════════
//  Individual report renderers
// ═══════════════════════════════════════════════════════════════

// ─── Sales summary ─────────────────────────────────────────────
const RSalesSummary = ({ params, range }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportSummaryUrl, { params }).then((r) => setData(r.data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  if (!data)   return <ErrorPanel message="Failed to load summary" />;

  const rows = [{
    period: `${range.from} to ${range.to}`,
    revenue: data.revenue, cogs: data.cost_of_goods_sold,
    gross_profit: data.gross_profit, expenses: data.expenses,
    net_profit: data.net_profit, sales_count: data.sales_count,
  }];
  return (
    <div className="panel">
      <ExportBar filename={`sales-summary_${range.from}_${range.to}`} rootName="SalesSummary" rowName="Summary" rows={rows} printTitle="Sales Summary" meta={{ generated: new Date().toISOString(), from: range.from, to: range.to }} />
      <div className="kpi-row">
        <StatCard label="Revenue"      val={fmtMoney(data.revenue)}              hint={`${fmtNumber(data.sales_count)} sales`} tone="brand"   icon="💵" />
        <StatCard label="Cost of goods"val={fmtMoney(data.cost_of_goods_sold)}   hint="COGS"                                   tone="warn"    icon="📉" />
        <StatCard label="Gross profit" val={fmtMoney(data.gross_profit)}         hint="Revenue − COGS"                          tone="accent"  icon="📈" />
        <StatCard label="Expenses"     val={fmtMoney(data.expenses)}             hint={`${range.from} → ${range.to}`}          tone="warn"    icon="🧾" />
      </div>
      <div className="rpt-hero">
        <div className="rpt-hero-lbl">Net profit · {range.from} → {range.to}</div>
        <div className="rpt-hero-val">{fmtMoney(data.net_profit)}</div>
        <div className="rpt-hero-sub">Gross profit {fmtMoney(data.gross_profit)} − Expenses {fmtMoney(data.expenses)}</div>
      </div>
    </div>
  );
};

// ─── Sales by day ──────────────────────────────────────────────
const RSalesByDay = ({ params, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportSalesByDayUrl, { params }).then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  const chartData = rows.map((r) => ({
    day: String(r.day).slice(5, 10),
    revenue: Number(r.revenue) || 0,
    sales: Number(r.sales_count) || 0,
  }));
  const totalRevenue = rows.reduce((s, r) => s + Number(r.revenue), 0);
  const totalSales   = rows.reduce((s, r) => s + Number(r.sales_count), 0);
  return (
    <div className="panel">
      <ExportBar filename={`sales-by-day_${range.from}_${range.to}`} rootName="SalesByDay" rowName="Day" rows={rows} printTitle="Sales by Day" meta={{ from: range.from, to: range.to }} />
      <div className="kpi-row">
        <StatCard label="Total revenue" val={fmtMoney(totalRevenue)} hint={`${fmtNumber(totalSales)} sales`} tone="brand"  icon="💵" />
        <StatCard label="Days"          val={fmtNumber(rows.length)} hint="with data"                        tone="accent" icon="📅" />
        <StatCard label="Best day"      val={rows.length ? fmtMoney(Math.max(...rows.map(r => Number(r.revenue)))) : '—'} hint="peak revenue" tone="success" icon="🏆" />
        <StatCard label="Avg / day"     val={rows.length ? fmtMoney(totalRevenue / rows.length) : '—'}       hint=""                                tone="warn"   icon="📊" />
      </div>
      {chartData.length === 0 ? <NoData /> : (
        <div style={{ width: '100%', height: 320, marginBottom: 18 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="rd" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--brand)" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="var(--brand)" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="day" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }} formatter={(v) => [fmtMoney(v), 'Revenue']} />
              <Area type="monotone" dataKey="revenue" stroke="var(--brand)" strokeWidth={2} fill="url(#rd)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}
      <RptTable
        head={['Date', 'Revenue', 'Sales count']}
        rows={rows.map((r) => [String(r.day).slice(0, 10), fmtMoney(r.revenue), fmtNumber(r.sales_count)])}
      />
    </div>
  );
};

// ─── Top products ──────────────────────────────────────────────
const RTopProducts = ({ params, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportTopProductsUrl, { params }).then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  const chartData = rows.slice(0, 10).map((r) => ({
    name: (r.product_name || '').length > 15 ? (r.product_name || '').slice(0, 14) + '…' : r.product_name,
    revenue: Number(r.revenue) || 0,
  }));
  return (
    <div className="panel">
      <ExportBar filename={`top-products_${range.from}_${range.to}`} rootName="TopProducts" rowName="Product" rows={rows} printTitle="Top Products" meta={{ from: range.from, to: range.to }} />
      {chartData.length === 0 ? <NoData /> : (
        <div style={{ width: '100%', height: 320, marginBottom: 18 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="name" angle={-25} textAnchor="end" tick={{ fill: 'var(--mute)', fontSize: 11 }} interval={0} height={60} />
              <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }} formatter={(v) => [fmtMoney(v), 'Revenue']} />
              <Bar dataKey="revenue" fill="var(--brand)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <RptTable
        head={['#', 'Product', 'Qty sold', 'Revenue', 'COGS', 'Gross profit']}
        rows={rows.map((r, i) => [i + 1, r.product_name, fmtNumber(r.qty_sold), fmtMoney(r.revenue), fmtMoney(r.cogs), fmtMoney(r.gross_profit)])}
      />
    </div>
  );
};

// ─── Sales by cashier ──────────────────────────────────────────
const RSalesByCashier = ({ params, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportSalesByCashierUrl, { params }).then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  const chartData = rows.slice(0, 10).map((r) => ({
    name: (r.name || 'Unknown').split(' ')[0],
    revenue: Number(r.revenue) || 0,
  }));
  return (
    <div className="panel">
      <ExportBar filename={`sales-by-cashier_${range.from}_${range.to}`} rootName="SalesByCashier" rowName="Cashier" rows={rows} printTitle="Sales by Cashier" meta={{ from: range.from, to: range.to }} />
      {chartData.length === 0 ? <NoData /> : (
        <div style={{ width: '100%', height: 280, marginBottom: 18 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }} formatter={(v) => [fmtMoney(v), 'Revenue']} />
              <Bar dataKey="revenue" fill="var(--accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <RptTable
        head={['Cashier', 'Branch', 'Sales', 'Revenue', 'Avg ticket', 'Discount given']}
        rows={rows.map((r) => [r.name || 'Unknown', r.branch_name || '—', fmtNumber(r.sales_count), fmtMoney(r.revenue), fmtMoney(r.avg_ticket), fmtMoney(r.discount_given)])}
      />
    </div>
  );
};

// ─── Sales by branch (super only) ──────────────────────────────
const RSalesByBranch = ({ params, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportSalesByBranchUrl, { params }).then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  const chartData = rows.map((r) => ({ name: r.branch_name, revenue: Number(r.revenue) || 0, profit: Number(r.gross_profit) || 0 }));
  return (
    <div className="panel">
      <ExportBar filename={`sales-by-branch_${range.from}_${range.to}`} rootName="SalesByBranch" rowName="Branch" rows={rows} printTitle="Sales by Branch" meta={{ from: range.from, to: range.to }} />
      {chartData.length === 0 ? <NoData /> : (
        <div style={{ width: '100%', height: 300, marginBottom: 18 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line)" />
              <XAxis dataKey="name" tick={{ fill: 'var(--mute)', fontSize: 11 }} />
              <YAxis tick={{ fill: 'var(--mute)', fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v} />
              <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }} formatter={(v) => fmtMoney(v)} />
              <Legend />
              <Bar dataKey="revenue" name="Revenue" fill="var(--brand)"  radius={[6, 6, 0, 0]} />
              <Bar dataKey="profit"  name="Profit"  fill="var(--accent)" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
      <RptTable
        head={['Branch', 'Sales', 'Revenue', 'COGS', 'Gross profit']}
        rows={rows.map((r) => [r.branch_name, fmtNumber(r.sales_count), fmtMoney(r.revenue), fmtMoney(r.cogs), fmtMoney(r.gross_profit)])}
      />
    </div>
  );
};

// ─── Inventory ─────────────────────────────────────────────────
const RInventory = ({ params, branchId, branchName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportInventoryUrl, { params: branchId ? { branch_id: branchId } : {} })
      .then((r) => setData(r.data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [branchId]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  if (!data)   return <ErrorPanel message="Failed to load inventory report" />;

  const low = data.low_stock || [];
  return (
    <div className="panel">
      <ExportBar filename={`inventory_${branchName || 'all'}_${todayISO()}`} rootName="InventoryReport" rowName="Item" rows={low} printTitle="Inventory Report" meta={{ generated: new Date().toISOString(), branch: branchName || 'All branches' }} />
      <div className="kpi-row">
        <StatCard label="Total units"      val={fmtNumber(data.aggregate?.total_units)}         hint="on hand"          tone="brand"  icon="📦" />
        <StatCard label="Stock @ cost"     val={fmtMoney(data.aggregate?.stock_value_cost)}     hint="capital tied up"  tone="accent" icon="💰" />
        <StatCard label="Stock @ retail"   val={fmtMoney(data.aggregate?.stock_value_retail)}   hint="if all sold"      tone="success" icon="🏷️" />
        <StatCard label="Low / OOS items"  val={fmtNumber(low.length)}                          hint="at or below reorder" tone="warn"   icon="⚠️" />
      </div>
      <h3 style={{ margin: '14px 0 8px', fontSize: 15 }}>Reorder list</h3>
      {low.length === 0 ? <div className="empty-state" style={{ padding: 30 }}><div className="ic">🎉</div><h4>Nothing to reorder</h4></div> : (
        <RptTable
          head={['Product', 'SKU', ...(branchId ? [] : ['Branch']), 'On hand', 'Available', 'Reorder level']}
          rows={low.map((r) => [r.name, r.sku, ...(branchId ? [] : [r.branch_name]), `${fmtNumber(r.qty_on_hand)} ${r.unit || ''}`, fmtNumber(r.qty_available), fmtNumber(r.reorder_level)])}
        />
      )}
    </div>
  );
};

// ─── P&L (reuses summary endpoint) ─────────────────────────────
const RPnL = ({ params, range }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportSummaryUrl, { params }).then((r) => setData(r.data))
      .catch(() => setData(null)).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  if (!data)   return <ErrorPanel message="Failed to load P&L" />;

  const rows = [
    { label: 'Revenue',                 amount: Number(data.revenue) || 0 },
    { label: 'Cost of goods sold',      amount: -(Number(data.cost_of_goods_sold) || 0) },
    { label: 'Gross profit',            amount: Number(data.gross_profit) || 0 },
    { label: 'Operating expenses',      amount: -(Number(data.expenses) || 0) },
    { label: 'Net profit',              amount: Number(data.net_profit) || 0 },
  ];
  return (
    <div className="panel">
      <ExportBar filename={`pnl_${range.from}_${range.to}`} rootName="PnL" rowName="Line" rows={rows} printTitle="Profit and Loss" meta={{ from: range.from, to: range.to }} />
      <div className="rpt-pnl">
        {rows.map((r, i) => (
          <div key={i} className={`rpt-pnl-row ${r.label === 'Gross profit' ? 'sub' : ''} ${r.label === 'Net profit' ? 'total' : ''}`}>
            <span>{r.label}</span>
            <strong style={{ color: r.amount < 0 ? 'var(--danger)' : (r.label === 'Net profit' ? 'var(--brand)' : 'var(--ink)') }}>
              {fmtMoney(Math.abs(r.amount))}
              {r.amount < 0 ? ' (−)' : ''}
            </strong>
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Expenses by category ──────────────────────────────────────
const RExpenses = ({ params, range }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    axios.get(reportExpensesByCategoryUrl, { params }).then((r) => setRows(r.data?.results || []))
      .catch(() => setRows([])).finally(() => setLoading(false));
  }, [JSON.stringify(params)]);   // eslint-disable-line

  if (loading) return <LoadingPanel />;
  const total = rows.reduce((s, r) => s + Number(r.total), 0);
  const pieColors = ['var(--brand)', 'var(--accent)', 'var(--warn)', 'var(--danger)', 'var(--info)', 'var(--success)', '#a855f7', '#ec4899'];
  const chart = rows.map((r) => ({ name: r.category, value: Number(r.total) }));
  return (
    <div className="panel">
      <ExportBar filename={`expenses_${range.from}_${range.to}`} rootName="Expenses" rowName="Category" rows={rows} printTitle="Expenses by Category" meta={{ from: range.from, to: range.to }} />
      <div className="kpi-row">
        <StatCard label="Total expenses" val={fmtMoney(total)}       hint={`${fmtNumber(rows.length)} categorie${rows.length === 1 ? '' : 's'}`} tone="warn" icon="💸" />
        <StatCard label="Biggest bucket" val={rows[0]?.category || '—'} hint={rows[0] ? fmtMoney(rows[0].total) : ''}                            tone="brand"  icon="🥇" />
      </div>
      {chart.length === 0 ? <NoData /> : (
        <div style={{ width: '100%', height: 320, marginBottom: 18 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={chart} dataKey="value" nameKey="name" outerRadius={110} label={(e) => `${e.name} ${(e.percent * 100).toFixed(0)}%`}>
                {chart.map((_, i) => <Cell key={i} fill={pieColors[i % pieColors.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 8, color: 'var(--ink)' }} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
      <RptTable
        head={['Category', 'Entries', 'Total', 'Share']}
        rows={rows.map((r) => [r.category, fmtNumber(r.count), fmtMoney(r.total), total > 0 ? `${((Number(r.total) / total) * 100).toFixed(1)}%` : '—'])}
      />
    </div>
  );
};

// ─── Small building blocks ─────────────────────────────────────
const LoadingPanel = () => <div className="empty-state"><div className="ic">⏳</div><h4>Running the report…</h4></div>;
const ErrorPanel   = ({ message }) => <div className="notice"><strong>{message}</strong></div>;
const NoData       = () => <div className="empty-state" style={{ padding: 30 }}><div className="ic">📄</div><h4>No data in this range</h4><p>Widen the date range or check a different branch.</p></div>;

const StatCard = ({ label, val, hint, tone, icon }) => (
  <div className="stat">
    <div className="lbl">{label}</div>
    <div className="val">{val}</div>
    <div className="hint">{hint}</div>
    <div className="ic" style={
      tone === 'brand'   ? { background: 'var(--brand-soft)',   color: 'var(--brand)' }
    : tone === 'accent'  ? { background: 'var(--accent-soft)',  color: 'var(--accent)' }
    : tone === 'warn'    ? { background: 'var(--warn-soft)',    color: 'var(--warn)' }
    : tone === 'success' ? { background: 'var(--success-soft)', color: 'var(--success)' }
    : {}}>{icon}</div>
  </div>
);

const RptTable = ({ head, rows }) => (
  <div className="tbl-wrap">
    <table className="tbl">
      <thead><tr>{head.map((h, i) => <th key={i} className={i > 1 ? 'right' : ''}>{h}</th>)}</tr></thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) => <td key={j} className={j > 1 ? 'right num' : ''}>{c}</td>)}
          </tr>
        ))}
        {rows.length === 0 && <tr><td colSpan={head.length} style={{ textAlign: 'center', color: 'var(--mute)', padding: 24 }}>No rows.</td></tr>}
      </tbody>
    </table>
  </div>
);

export default Reports;
